import { useCreateOrUpdateTirageInBibliography } from "@/api/related-fields";
import {
  applyFieldConfigOverrides,
  FormLayoutElement,
} from "@/config/forms/general";
import { TirageInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const TirageInBibliographyDrawer = ({
                                      fields,
                                      data,
                                      queryId,
                                      type,
                                      open,
                                      onOpenChange,
                                    }: {
  fields: FormLayoutElement[] | undefined;
  data: TirageInBibliography;
  queryId: string;
  type: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    tirageDansBibliographieDescriptionReproduitPage: z.string().optional(),
    tirageDansBibliographieDescriptionReproduitPlanche: z.string().optional(),
    tirageDansBibliographieDescriptionPlancheNonNumerotee: z
      .boolean()
      .optional(),
    tirageDansBibliographieDescriptionCitePage: z.string().optional(),
    tirageDansBibliographieDescriptionListePage: z.string().optional(),
    tirageDansBibliographieDescriptionSousNumero: z.string().optional(),
    tirageDansBibliographieDescriptionPhotographe: z.string().optional(),
    tirageDansBibliographieDescriptionReproductionCouleur: z
      .boolean()
      .optional(),
    tirageDansBibliographieDescriptionReproductionCouverture: z
      .boolean()
      .optional(),
    tirageDansBibliographieDescriptionNonPagine: z.boolean().optional(),
    tirageDansBibliographieDescriptionTexteLibre: z.string().optional(),
    tirageDansBibliographieNotes: z.string().optional(),
    bibliography: z.object({
      bibliographieDescriptionNumeroBibliographie: z.string().optional(),
      bibliographieDescriptionResumeLong: z.string().optional(),
    }),
  });

  const { mutate: createOrUpdateTirageInBibliography } =
    useCreateOrUpdateTirageInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      bibliography: data.bibliography,
      tirageDansBibliographieDescriptionReproduitPage:
      data.tirageDansBibliographieDescriptionReproduitPage,
      tirageDansBibliographieDescriptionReproduitPlanche:
      data.tirageDansBibliographieDescriptionReproduitPlanche,
      tirageDansBibliographieDescriptionPlancheNonNumerotee:
      data.tirageDansBibliographieDescriptionPlancheNonNumerotee,
      tirageDansBibliographieDescriptionCitePage:
      data.tirageDansBibliographieDescriptionCitePage,
      tirageDansBibliographieDescriptionListePage:
      data.tirageDansBibliographieDescriptionListePage,
      tirageDansBibliographieDescriptionSousNumero:
      data.tirageDansBibliographieDescriptionSousNumero,
      tirageDansBibliographieDescriptionPhotographe:
      data.tirageDansBibliographieDescriptionPhotographe,
      tirageDansBibliographieDescriptionReproductionCouleur:
      data.tirageDansBibliographieDescriptionReproductionCouleur,
      tirageDansBibliographieDescriptionReproductionCouverture:
      data.tirageDansBibliographieDescriptionReproductionCouverture,
      tirageDansBibliographieDescriptionNonPagine:
      data.tirageDansBibliographieDescriptionNonPagine,
      tirageDansBibliographieDescriptionTexteLibre:
      data.tirageDansBibliographieDescriptionTexteLibre,
      tirageDansBibliographieNotes: data.tirageDansBibliographieNotes,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdateTirageInBibliography({
      id: data.id,
      tirageId: data.tirage.id,
      bibliographyId: data.bibliography.id,
      ...formData,
    });
  };
  const isFromBibliography = type === "bibliography";
  const previewConfig = isFromBibliography
    ? {
        attachments: data.tirage?.attachments ?? [],
        type: "tirage" as const,
        alt: data.tirage?.tirageDescriptionNumeroEnregistrement,
      }
    : {
        attachments: data.bibliography?.attachments ?? [],
        type: "bibliography" as const,
        alt:
          data.bibliography?.bibliographieDescriptionNumeroBibliographie ??
          data.bibliography?.bibliographieDescriptionTitre,
      };
  const enhancedFields = applyFieldConfigOverrides(fields ?? [], {
    tirageDansBibliographieDescriptionTexteLibre: {
      expandOnOverflow: true,
    },
  });

  return (
    <LinksDrawerLayout
      form={form}
      fields={enhancedFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default TirageInBibliographyDrawer;
